import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/useMobile";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useCompanyType } from "@/hooks/useCompanyType";
import { DesktopSidebar } from "./layout/DesktopSidebar";
import { Header } from "./layout/Header";
import { getNavigationItems } from "./layout/navigationItems";
import { FixedUserBadge } from "./layout/FixedUserBadge";
import { useSupabaseNetworkStatus } from "@/hooks/useSupabaseNetworkStatus";
import { MobileSidebar } from "./sidebar/MobileSidebar";

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  useSupabaseNetworkStatus();

  // Usar sidebar:pinned para sincronizar com DesktopSidebar
  // Este estado representa se o sidebar está FIXO (pinned)
  const [isSidebarPinned, setIsSidebarPinned] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('sidebar:pinned');
      return stored === 'true';
    }
    return false;
  });

  // State to track hover expansion for content area adaptation
  const [isSidebarHoverExpanded, setIsSidebarHoverExpanded] = useState(false);

  // Estado computado para determinar se o sidebar deve ser considerado expandido
  // Isso simplifica a lógica e evita conflitos
  const isSidebarEffectiveExpanded = isSidebarPinned || isSidebarHoverExpanded;

  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  const { profile, logout, user } = useAuth();
  const { companyType } = useCompanyType(profile?.company_id);

  // Compute navigation items based on company type
  const navItems = useMemo(() => {
    return getNavigationItems(companyType);
  }, [companyType]);

  // Sincronizar com localStorage usando sidebar:pinned
  useEffect(() => {
    localStorage.setItem('sidebar:pinned', isSidebarPinned ? 'true' : 'false');
  }, [isSidebarPinned]);

  // Listen for hover state changes from DesktopSidebar
  // Usar debounce para evitar múltiplas atualizações rápidas
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleSidebarHoverChange = (event: CustomEvent) => {
      // Debounce para evitar flickering
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsSidebarHoverExpanded(event.detail.isHoverExpanded);
      }, 50); // 50ms de debounce
    };

    // Add event listener
    window.addEventListener('sidebarHoverChange', handleSidebarHoverChange as EventListener);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('sidebarHoverChange', handleSidebarHoverChange as EventListener);
      clearTimeout(timeoutId);
    };
  }, []);

  // CSS Variable Synchronization for dynamic width system
  // This effect bridges the JavaScript state with the CSS system
  useEffect(() => {
    const root = document.documentElement;

    // Set CSS variable based on sidebar PINNED state, not expanded state
    // This ensures content only adapts when sidebar is fixed, not on hover
    const sidebarWidth = isSidebarPinned ? '264px' : '64px';
    root.style.setProperty('--sidebar-current-width', sidebarWidth);

    // Optional: Set will-change for better performance during transitions
    root.style.setProperty('--sidebar-will-change', isSidebarPinned ? 'width' : 'auto');

    // Add CSS class to indicate pinned state for styling
    if (isSidebarPinned) {
      root.classList.add('sidebar-pinned');
      root.classList.remove('sidebar-not-pinned');
    } else {
      root.classList.add('sidebar-not-pinned');
      root.classList.remove('sidebar-pinned');
    }

    // For debugging purposes (can be removed in production)
    if (process.env.NODE_ENV === 'development') {
      console.log(`[MainLayout] CSS variables updated:`, {
        '--sidebar-current-width': sidebarWidth,
        '--sidebar-will-change': root.style.getPropertyValue('--sidebar-will-change'),
        'isSidebarPinned': isSidebarPinned,
        'isSidebarEffectiveExpanded': isSidebarEffectiveExpanded,
        'cssClass': isSidebarPinned ? 'sidebar-pinned' : 'sidebar-not-pinned'
      });
    }

    // Cleanup function to reset styles on unmount
    return () => {
      root.style.removeProperty('--sidebar-current-width');
      root.style.removeProperty('--sidebar-will-change');
      root.classList.remove('sidebar-pinned', 'sidebar-not-pinned');
    };
  }, [isSidebarPinned, isSidebarEffectiveExpanded]);

  // Performance optimization: Add containment for the main layout grid
  useEffect(() => {
    const mainLayout = document.querySelector('.main-layout-grid');
    if (mainLayout) {
      // Use contain property to isolate layout recalculations
      (mainLayout as HTMLElement).style.contain = 'layout style paint';

      return () => {
        (mainLayout as HTMLElement).style.removeProperty('contain');
      };
    }
  }, []);

  const toggleSidebar = () => {
    console.log('[MainLayout] toggleSidebar called, current isSidebarPinned:', isSidebarPinned);
    setIsSidebarPinned((prev) => {
      const newState = !prev;
      console.log('[MainLayout] Setting isSidebarPinned to:', newState);
      return newState;
    });
  };

  const handleOpenNewTransactionDialog = () => {
    if (location.pathname === "/transactions" || location.pathname.startsWith("/transactions/")) {
      const event = new CustomEvent("openNewTransactionDialog");
      window.dispatchEvent(event);
    } else {
      // Navigate to a generic transactions path if not already on one
      const targetPath = location.pathname.startsWith('/transactions') ? location.pathname : "/transactions";
      navigate(targetPath);

      setTimeout(() => {
        const event = new CustomEvent("openNewTransactionDialog");
        window.dispatchEvent(event);
      }, 100);
    }
  };

  return (
    <div className={cn(
      "main-layout-grid bg-background",
      !isMobile && isSidebarPinned ? "sidebar-expanded" : "sidebar-collapsed",
      isMobile && isSidebarPinned && "mobile-menu-open"
    )}>
      {/* Fixed Header */}
      <Header
        profile={profile}
        user={user}
        logout={logout}
        isMobile={isMobile}
        isSidebarExpanded={!isMobile && isSidebarPinned}
        toggleSidebar={() => setIsSidebarPinned(true)}
        onAddNewTransaction={handleOpenNewTransactionDialog}
      />

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div className="sidebar-column">
          <DesktopSidebar
            isSidebarExpanded={isSidebarPinned}
            toggleSidebar={toggleSidebar}
            navItems={navItems}
            profile={profile}
            user={user}
          />
        </div>
      )}

      {/* Mobile Sidebar - Overlay */}
      {isMobile && (
        <div className={cn("sidebar-column", isSidebarPinned && "mobile-open")}>
          <MobileSidebar
            profile={profile}
            user={user}
            navItems={navItems}
            location={location}
            onClose={() => setIsSidebarPinned(false)}
            logout={logout}
          />
        </div>
      )}

      {/* Content Area */}
      <div className="content-area">
        <div className="content-wrapper">
          {children}
        </div>
      </div>

      {/* Mobile User Badge - Fixed at bottom */}
      {isMobile && (
        <FixedUserBadge
          profile={profile}
          user={user}
        />
      )}
    </div>
  );
}
